import {environment} from '../../environments/environment';

/**
 * Build-time deployment target, selected by `environment.config` of the active environment file.
 *
 * - `OnPrem`: MIM 2016 through the on-premises REST API; Windows authentication.
 * - `Cloud`: the Identity Director Service in Azure; Entra ID (MSAL) authentication.
 * - `Portal`: like Cloud, but the shell shows an endpoint switcher for the `endpoints[]` list.
 * - `IDService`: the Identity Director Service hosted locally / on-premises, serving this Portal
 *   on the same origin; Entra ID authentication.
 *
 * Every decision that depends on the target goes through the helpers below instead of comparing
 * `environment.config` strings in components and modules.
 */
export type DeploymentConfig = 'OnPrem' | 'Cloud' | 'Portal' | 'IDService';

/** Runtime kind of the backend behind an endpoint: MIM + REST API, or the Identity Director Service. */
export type EndpointKind = 'mim' | 'idservice';

export function deploymentConfig(): DeploymentConfig {
  return environment.config as DeploymentConfig;
}

/** Entra ID (MSAL) login, guard and bearer interceptor are active. */
export function isEntraAuth(config: DeploymentConfig = deploymentConfig()): boolean {
  return config !== 'OnPrem';
}

/** The shell shows the endpoint switcher instead of connecting to `ServerApiUrl` directly. */
export function showsEndpointSwitcher(config: DeploymentConfig = deploymentConfig()): boolean {
  return config === 'Portal';
}

/** Trigger the MSAL login redirect when no account is signed in. */
export function autoLoginRedirect(config: DeploymentConfig = deploymentConfig()): boolean {
  return config === 'Cloud' || config === 'Portal' || config === 'IDService';
}

/** The MIM install page (`/install`) exists and administrators are sent there when no UI configuration is found. */
export function hasInstallPage(config: DeploymentConfig = deploymentConfig()): boolean {
  return config === 'OnPrem' || config === 'Portal';
}

/** Kind of the default endpoint (`ServerApiUrl`) before the user switches endpoints. */
export function defaultEndpointKind(config: DeploymentConfig = deploymentConfig()): EndpointKind {
  return config === 'Cloud' || config === 'IDService' ? 'idservice' : 'mim';
}

/** Resolves an absolute or relative `ServerApiUrl` (for example `/api/`) against the page origin. */
export function resolveApiUrl(apiUrl: string, base: string = window.location.origin): URL {
  return new URL(apiUrl, base);
}

/** `.../api/` becomes `.../admin/`; only a trailing `api` segment is replaced. */
export function adminUrl(apiUrl: string): string {
  return apiUrl.replace(/api\/?$/, 'admin/');
}
